import { useState } from 'react';
import { classificationsApi } from '../utils/api';
import { classificationColor, nextClassificationColor } from '../utils/classificationColors';
import { deleteClassificationDialog } from '../utils/confirmCopy';
import ConfirmModal from './ConfirmModal';
import Icon from './Icon';
import ExpandCaret from './ExpandCaret';
import PresetClassificationPicker from './PresetClassificationPicker';

// ניהול סיווג פריטים — rename, recolour, delete and create item-type
// classifications. Nothing else.
//
// This is the identity half of what BudgetSheet used to be. When targets moved
// to קטגוריות, that sheet went with them to /budget — and it was the ONLY
// place an item-type classification could be renamed, recoloured or deleted. Moving
// it without leaving this behind would have recreated commit 1ce9b52 exactly:
// ClassificationManager deleted, the capability unreachable, and it shipped.
//
// It lives INLINE on /item-classifications, collapsed by default, as a card matching
// הגדרת קטגוריות ויעדים on /budget — same collapsible-card idiom, same
// classes. On the page, not over it: managing classifications is setup you do while
// looking at the tagging table it feeds, and a modal hid that setup behind a
// click and an overlay you could not scroll against the list. Collapsed because
// tagging products is the daily work; renaming classifications is occasional.
//
// Every write is immediate. There is no save button because there is nothing to
// batch: renaming a classification is not scoped to a period, so a button labelled
// with one would be promising something false.
export default function ClassificationSheet({
  classifications = [], onRefresh, open = true, onToggle,
  // The starter list, and the action that creates from it. Both optional: the
  // sheet still works without them, it just does not offer the shortcut.
  presets = [], onCreatePresets,
}) {
  const [editing, setEditing] = useState(null);   // { id, value }
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  // True zero-classification landing: one button, nothing else, until clicked.
  // Once classifications.length > 0 this stops mattering — the full form below
  // always shows again.
  const [showAddInput, setShowAddInput] = useState(false);
  const isEmptyFirstLook = classifications.length === 0 && !showAddInput;

  // Collapsed, the header still has to answer "what is in here" — otherwise
  // folding the section away just hides the count behind a chevron.
  const headerSummary = classifications.length === 0
    ? ''
    : `${classifications.length} סיווגים`;

  const run = async (fn, failure) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await onRefresh?.();
    } catch (e) {
      setError(e?.response?.data?.error || failure);
    } finally {
      setBusy(false);
    }
  };

  const commitRename = (cat) => {
    const trimmed = (editing?.value || '').trim();
    setEditing(null);
    if (!trimmed || trimmed === cat.name) return;
    run(() => classificationsApi.update(cat.id, {
      name: trimmed, color: cat.color, display_order: cat.display_order,
    }), 'שגיאה בשינוי השם');
  };

  const recolor = (cat, value) => {
    if (!value || value.toLowerCase() === (cat.color || '').toLowerCase()) return;
    run(() => classificationsApi.update(cat.id, {
      name: cat.name, color: value, display_order: cat.display_order,
    }), 'שגיאה בשינוי הצבע');
  };

  const remove = (cat) => setConfirm({
    ...deleteClassificationDialog({ name: cat.name, productCount: cat.product_count || 0 }),
    onConfirm: () => {
      setConfirm(null);
      run(() => classificationsApi.delete(cat.id), 'שגיאה במחיקה');
    },
  });

  const add = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    run(async () => {
      await classificationsApi.create({ name: trimmed, color: nextClassificationColor(classifications) });
      setNewName('');
    }, 'שגיאה בהוספה');
  };

  return (
    <>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <section className={`card category-lines-card${open ? '' : ' is-collapsed'}`}>
        <button
          type="button" className="category-lines-toggle"
          onClick={onToggle} aria-expanded={open} aria-controls="classification-sheet-body"
        >
          <span className="category-lines-title">ניהול סיווג פריטים</span>
          <span className="category-lines-summary">{headerSummary}</span>
          <ExpandCaret open={open} size={14} />
        </button>

        {open && (
        <div id="classification-sheet-body" dir="rtl">
          <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#94a3b8' }}>
            סיווג הפריט קובעת מה המוצר. התקציב נקבע בנפרד, לפי קטגוריות.
          </p>

          {error && (
            <div className="badge-red" style={{ display: 'block', marginBottom: 12, padding: '8px 12px', borderRadius: 8 }}>
              {error}
            </div>
          )}

          {/* True first look at a zero-classification restaurant: one button,
              nothing else — no empty table, no preset grid competing with it.
              Clicking it reveals the normal form below (and the preset
              shortcut with it), so nothing reachable today gets removed. */}
          {isEmptyFirstLook ? (
            <button type="button" className="btn btn-primary" onClick={() => setShowAddInput(true)}>
              הוספת סיווג ראשון
            </button>
          ) : (
          <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input
              className="form-control" placeholder="סיווג חדש" aria-label="שם סיווג חדש"
              value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              style={{ width: 200, padding: '4px 8px' }}
              autoFocus={showAddInput}
            />
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={add} disabled={busy || !newName.trim()}>הוסף</button>
          </div>

          {/* The starter list, reachable FOREVER — not only on the first-run
              card, which unmounts the moment a restaurant has one
              classification. Applying a single suggestion used to be enough to
              lose this permanently, leaving retyping canonical names from
              memory as the only route, and a name that misses the vocabulary
              stores canonical_id NULL and drops out of the suggestion engine.
              Hidden entirely once every preset exists (the picker returns
              null), so a finished restaurant sees nothing. */}
          {onCreatePresets && (
            <div style={{ marginBottom: 12 }}>
              <PresetClassificationPicker
                presets={presets}
                existing={classifications}
                onCreate={ids => run(() => onCreatePresets(ids), 'שגיאה ביצירת הסיווגים')}
                busy={busy}
                variant="collapsed"
              />
            </div>
          )}

          <div className="table-wrapper sheet-table" style={{ overflowY: 'auto', maxHeight: 338 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }} />
                  <th>סיווג</th>
                  <th style={{ width: 90 }}>מוצרים</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {classifications.map((c, i) => (
                  <tr key={c.id}>
                    <td>
                      {/* `||` not `??`: legacy rows carry color:'' , which is not
                          nullish and would render as black. */}
                      <input
                        type="color" aria-label={`צבע עבור ${c.name}`}
                        value={(c.color) || classificationColor(c, i)}
                        onChange={e => recolor(c, e.target.value)}
                        style={{ width: 26, height: 22, padding: 0, border: 0, background: 'none' }}
                      />
                    </td>
                    <td>
                      {editing?.id === c.id ? (
                        <input
                          className="form-control" autoFocus
                          aria-label={`שם עבור ${c.name}`}
                          value={editing.value}
                          onChange={e => setEditing({ id: c.id, value: e.target.value })}
                          onBlur={() => commitRename(c)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(c); }
                            if (e.key === 'Escape') setEditing(null);
                          }}
                          style={{ padding: '2px 6px' }}
                        />
                      ) : (
                        <span
                          role="button" tabIndex={0}
                          onClick={() => setEditing({ id: c.id, value: c.name })}
                          onKeyDown={e => { if (e.key === 'Enter') setEditing({ id: c.id, value: c.name }); }}
                          title="לחץ לשינוי השם"
                          style={{ cursor: 'text' }}
                        >
                          {c.name}
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--ink-muted)' }}>{c.product_count ?? 0}</td>
                    <td style={{ padding: '6px 4px' }}>
                      <button
                        type="button" className="btn btn-secondary"
                        onClick={() => remove(c)} disabled={busy}
                        aria-label={`מחק את ${c.name}`} title="מחק סיווג"
                        style={{ padding: '4px 7px', color: '#b91c1c' }}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {classifications.length === 0 && (
                  <tr><td colSpan={4} style={{ color: '#94a3b8', padding: '14px 8px' }}>
                    עדיין אין סיווג פריטים.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: 10, marginBottom: 0, fontSize: 11.5, color: '#94a3b8' }}>
            שינויים נשמרים מיד. מחיקת סיווג מעבירה את המוצרים שלה ל״לא מסווג״ — היא לא מוחקת מוצרים.
          </p>
          </>
          )}
        </div>
        )}
      </section>
    </>
  );
}
